import type { Locale } from "@/i18n/strings";

/* Member identity presentation, transcribed from /legacy.
 *
 * displayName / initials: Individual Member Panel.dc.html:1496-1504 -- the
 * name if there is one, else the email's local part with separators turned to
 * spaces and each word capitalised, else a neutral label. Legacy's own
 * comment says it: "Never a hardcoded demo name."
 *
 * Plan badge colours: EjarMasr.dc.html:1223 planBadgeMap.
 */

export function displayName(
  name: string | null | undefined,
  email: string | null | undefined,
  fallback: string,
): string {
  const given = (name ?? "").trim();
  if (given) return given;
  const local = (email ?? "").split("@")[0].replace(/[._]+/g, " ").trim();
  if (local) return local.replace(/\b\w/g, (c) => c.toUpperCase());
  return fallback;
}

export function firstName(display: string, fallback: string): string {
  return display === fallback ? "" : display.split(" ")[0];
}

/** Up to two initials. Arabic names have no case, so toUpperCase is a no-op
 *  there and the first letters are used as written. */
export function initials(display: string, fallback: string): string {
  if (display === fallback) return fallback.slice(0, 1);
  // Only word-initial letters or digits count. Legacy used filter(Boolean),
  // which let punctuation through: a name like "TEST — Account" rendered its
  // avatar as "T—". Dashes, bullets and brackets are not initials.
  const words = display.split(/\s+/).filter((w) => /^[\p{L}\p{N}]/u.test(w));
  const picked = (words.length ? words : [display]).slice(0, 2);
  return picked.map((w) => [...w][0].toUpperCase()).join("");
}

export type PlanKey = "platinum" | "gold" | "silver" | "free";

export function planKey(plan: string | null | undefined): PlanKey {
  const p = (plan ?? "").toLowerCase();
  return p === "platinum" || p === "gold" || p === "silver" ? p : "free";
}

/** The plan name as the SERVER spells it -- shown, never translated, because
 *  the plan names are product names and legacy printed `planRaw` verbatim. */
export function planBadgeLabel(plan: string | null | undefined, locale: Locale): string {
  const raw = (plan ?? "").trim();
  if (raw) return raw;
  return locale === "ar" ? "مجاني" : "Free";
}
